/**
 * useEntityRecordStore
 *
 * Mounts the keyed `(entityId, recordId)` Valtio instance and returns its
 * snapshot plus the imperative writers bound to the current key.
 *
 * Refcounted: the same `(entityId, recordId)` shared by multiple consumers
 * subscribes to Firestore once; the last consumer triggers a 30s grace timer
 * before teardown. A re-acquire within the grace window reuses the instance.
 *
 * `recordId` may be `undefined` for "entity selected, no record" scopes —
 * only the entity slot loads. `entityId` may be empty (no scope) — the hook
 * no-ops the subscription and returns empty slots, so it stays safe to call
 * unconditionally.
 */
import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { DocId, Entity, EntityRecord } from '#types/System.types';
import {
    acquireEntityRecordStore,
    releaseEntityRecordStore,
    EMPTY_ENTITY_RECORD_STATE,
} from './entityRecordStore';
import { entityRecordActions } from './entityRecordActions';

export type EntityRecordStoreSlot<T> = {
    data: T | null;
    loading: boolean;
    error: Error | null;
};

export type UseEntityRecordStoreReturn = {
    entityId: string;
    recordId: string | undefined;
    entityGroupId: string | undefined;
    entityLocked: boolean;
    insideEntityRecordContext: boolean;
    entity: EntityRecordStoreSlot<Entity>;
    entityRecord: EntityRecordStoreSlot<EntityRecord>;
    setEntityRecord: (updates: Partial<EntityRecord>, options?: { merge?: boolean }) => Promise<void>;
    debouncedSetEntityRecord: (updates: Partial<EntityRecord>) => void;
    deleteEntityRecord: () => Promise<void>;
    setEntityLocked: (value: boolean) => void;
    selectEntityGroup: (groupId: string | undefined) => void;
};

const EMPTY_SLOT: EntityRecordStoreSlot<unknown> = {
    data: null,
    loading: false,
    error: null,
};

export const useEntityRecordStore = (
    entityId: string,
    recordId: string | undefined,
): UseEntityRecordStoreReturn => {
    const isBound = entityId !== '';
    const recordIsBound = isBound && recordId !== undefined;

    const entry = useMemo(
        () => (isBound ? acquireEntityRecordStore(entityId, recordId) : null),
        [isBound, entityId, recordId],
    );

    useEffect(() => {
        if (!isBound) return;
        return () => {
            releaseEntityRecordStore(entityId, recordId);
        };
    }, [isBound, entityId, recordId]);

    const snap = useSnapshot(entry?.state ?? EMPTY_ENTITY_RECORD_STATE);

    if (!isBound) {
        return {
            entityId,
            recordId,
            entityGroupId: undefined,
            entityLocked: false,
            insideEntityRecordContext: false,
            entity: EMPTY_SLOT as EntityRecordStoreSlot<Entity>,
            entityRecord: EMPTY_SLOT as EntityRecordStoreSlot<EntityRecord>,
            setEntityRecord: async () => undefined,
            debouncedSetEntityRecord: () => undefined,
            deleteEntityRecord: async () => undefined,
            setEntityLocked: () => undefined,
            selectEntityGroup: () => undefined,
        };
    }

    return {
        entityId,
        recordId,
        entityGroupId: snap.entityGroupId,
        entityLocked: snap.entityLocked,
        insideEntityRecordContext: true,
        entity: {
            data: snap.entity.data as Entity | null,
            loading: snap.entity.loading,
            error: snap.entity.error,
        },
        entityRecord: {
            data: snap.entityRecord.data as EntityRecord | null,
            loading: snap.entityRecord.loading,
            error: snap.entityRecord.error,
        },
        setEntityRecord: async (updates, options) => {
            if (!recordIsBound) return;
            await entityRecordActions.setEntityRecord(entityId, recordId, updates, options);
        },
        debouncedSetEntityRecord: (updates) => {
            if (!recordIsBound) return;
            entityRecordActions.debouncedSetEntityRecord(entityId, recordId, updates);
        },
        deleteEntityRecord: async () => {
            if (!recordIsBound) return;
            await entityRecordActions.deleteEntityRecord(entityId, recordId as DocId);
        },
        setEntityLocked: (value) => {
            entityRecordActions.setEntityLocked(entityId, recordId, value);
        },
        selectEntityGroup: (groupId) => {
            entityRecordActions.selectEntityGroup(entityId, recordId, groupId);
        },
    };
};
